import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Plus,
  RefreshCw,
  Loader2,
  Building,
  CreditCard,
  Calculator,
  TrendingDown,
  Landmark,
  Car,
  Monitor,
  Package,
  Home,
  HelpCircle,
  Percent,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASSET_CATEGORIES = [
  { value: 'furniture', label: 'Furniture & Fixtures', icon: Building },
  { value: 'equipment', label: 'Equipment', icon: Package },
  { value: 'computer', label: 'Computer & Electronics', icon: Monitor },
  { value: 'vehicle', label: 'Vehicles', icon: Car },
  { value: 'building', label: 'Building', icon: Home },
  { value: 'land', label: 'Land', icon: Landmark },
  { value: 'other', label: 'Other', icon: HelpCircle }
];

const LIABILITY_TYPES = [
  { value: 'loan', label: 'Loan' },
  { value: 'deposit', label: 'Security Deposit' },
  { value: 'advance', label: 'Advance Received' },
  { value: 'payable', label: 'Account Payable' },
  { value: 'other', label: 'Other' }
];

const AssetsLiabilitiesTab = ({ getAuthHeader }) => {
  const [activeSubTab, setActiveSubTab] = useState('assets');
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [depSchedule, setDepSchedule] = useState({ assets: [], summary: {} });
  const [loading, setLoading] = useState(false);
  
  // Dialogs
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showAddLiability, setShowAddLiability] = useState(false);
  
  // Forms
  const today = new Date().toISOString().split('T')[0];
  const [assetForm, setAssetForm] = useState({
    asset_name: '',
    asset_type: 'fixed_asset',
    category: 'computer',
    purchase_date: today,
    purchase_value: '',
    depreciation_rate: 10,
    depreciation_method: 'straight_line',
    location: '',
    serial_number: '',
    vendor_name: '',
    warranty_expiry: '',
    remarks: ''
  });
  
  const [liabilityForm, setLiabilityForm] = useState({
    liability_name: '',
    liability_type: 'loan',
    creditor_name: '',
    principal_amount: '',
    interest_rate: 10,
    start_date: today,
    maturity_date: '',
    payment_frequency: 'monthly',
    remarks: ''
  });

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/accounting/assets`, {
        headers: getAuthHeader()
      });
      setAssets(res.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchLiabilities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/accounting/liabilities`, {
        headers: getAuthHeader()
      });
      setLiabilities(res.data);
    } catch (error) {
      toast.error('Failed to fetch liabilities');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepreciationSchedule = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounting/depreciation/schedule`, {
        headers: getAuthHeader()
      });
      setDepSchedule(res.data);
    } catch (error) {
      console.error('Failed to fetch depreciation schedule');
    }
  };

  useEffect(() => {
    fetchAssets();
    fetchLiabilities();
    fetchDepreciationSchedule();
  }, []);

  const handleAddAsset = async () => {
    if (!assetForm.asset_name || !assetForm.purchase_value) {
      toast.error('Asset name and purchase value are required');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/accounting/assets`, {
        ...assetForm,
        purchase_value: parseFloat(assetForm.purchase_value)
      }, { headers: getAuthHeader() });
      
      toast.success('Asset added successfully');
      setShowAddAsset(false);
      setAssetForm({
        asset_name: '',
        asset_type: 'fixed_asset',
        category: 'computer',
        purchase_date: today,
        purchase_value: '',
        depreciation_rate: 10,
        depreciation_method: 'straight_line',
        location: '',
        serial_number: '',
        vendor_name: '',
        warranty_expiry: '',
        remarks: ''
      });
      fetchAssets();
      fetchDepreciationSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add asset');
    }
  };

  const handleAddLiability = async () => {
    if (!liabilityForm.liability_name || !liabilityForm.principal_amount) {
      toast.error('Liability name and principal amount are required');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/accounting/liabilities`, {
        ...liabilityForm,
        principal_amount: parseFloat(liabilityForm.principal_amount),
        interest_rate: parseFloat(liabilityForm.interest_rate)
      }, { headers: getAuthHeader() });
      
      toast.success('Liability added successfully');
      setShowAddLiability(false);
      setLiabilityForm({
        liability_name: '',
        liability_type: 'loan',
        creditor_name: '',
        principal_amount: '',
        interest_rate: 10,
        start_date: today,
        maturity_date: '',
        payment_frequency: 'monthly',
        remarks: ''
      });
      fetchLiabilities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add liability');
    }
  };

  const calculateDepreciation = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/accounting/depreciation/calculate`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Depreciation calculated: ₹${res.data.total_depreciation.toLocaleString()}`);
      fetchAssets();
      fetchDepreciationSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate depreciation');
    }
  };

  const calculateInterest = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/accounting/interest/calculate`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Interest calculated: ₹${res.data.total_interest.toLocaleString()}`);
      fetchLiabilities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate interest');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getCategoryIcon = (category) => {
    const cat = ASSET_CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : HelpCircle;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Assets & Liabilities</CardTitle>
              <CardDescription>Manage fixed assets, loans, and calculate depreciation/interest</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={calculateDepreciation} size="sm">
                <Calculator className="h-4 w-4 mr-2" />
                Run Depreciation
              </Button>
              <Button variant="outline" onClick={calculateInterest} size="sm">
                <Percent className="h-4 w-4 mr-2" />
                Calculate Interest
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <p className="text-sm text-blue-700">Total Assets</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatCurrency(depSchedule.summary?.total_purchase_value || assets.reduce((sum, a) => sum + a.purchase_value, 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4">
                <p className="text-sm text-green-700">Current Value</p>
                <p className="text-2xl font-bold text-green-900">
                  {formatCurrency(depSchedule.summary?.total_current_value || assets.reduce((sum, a) => sum + (a.current_value || a.purchase_value), 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="pt-4">
                <p className="text-sm text-orange-700">Total Depreciation</p>
                <p className="text-2xl font-bold text-orange-900">
                  {formatCurrency(depSchedule.summary?.total_accumulated_depreciation || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <p className="text-sm text-red-700">Total Liabilities</p>
                <p className="text-2xl font-bold text-red-900">
                  {formatCurrency(liabilities.reduce((sum, l) => sum + l.outstanding_amount, 0))}
                </p>
              </CardContent>
            </Card>
          </div>

          <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
            <TabsList className="grid grid-cols-3 w-full max-w-md">
              <TabsTrigger value="assets" data-testid="assets-tab">
                <Building className="h-4 w-4 mr-2" />
                Assets
              </TabsTrigger>
              <TabsTrigger value="liabilities" data-testid="liabilities-tab">
                <CreditCard className="h-4 w-4 mr-2" />
                Liabilities
              </TabsTrigger>
              <TabsTrigger value="depreciation" data-testid="depreciation-tab">
                <TrendingDown className="h-4 w-4 mr-2" />
                Depreciation
              </TabsTrigger>
            </TabsList>

            {/* Assets Tab */}
            <TabsContent value="assets">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Fixed Assets Register</h3>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={fetchAssets} size="sm">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                  <Button onClick={() => setShowAddAsset(true)} data-testid="add-asset-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Asset
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : assets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No assets recorded yet</p>
                  <p className="text-sm">Click "Add Asset" to add your first asset</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Asset Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Purchase Date</TableHead>
                      <TableHead className="text-right">Purchase Value</TableHead>
                      <TableHead className="text-right">Current Value</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assets.map((asset) => {
                      const CategoryIcon = getCategoryIcon(asset.category);
                      return (
                        <TableRow key={asset.id}>
                          <TableCell className="font-mono text-sm">{asset.asset_code}</TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                              {asset.asset_name}
                            </div>
                            {asset.serial_number && (
                              <span className="text-xs text-muted-foreground">S/N: {asset.serial_number}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {asset.category}
                            </Badge>
                          </TableCell>
                          <TableCell>{asset.purchase_date}</TableCell>
                          <TableCell className="text-right">{formatCurrency(asset.purchase_value)}</TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(asset.current_value)}
                          </TableCell>
                          <TableCell>
                            <Badge className={asset.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {asset.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Liabilities Tab */}
            <TabsContent value="liabilities">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Liabilities Register</h3>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={fetchLiabilities} size="sm">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                  <Button onClick={() => setShowAddLiability(true)} data-testid="add-liability-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Liability
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : liabilities.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No liabilities recorded yet</p>
                  <p className="text-sm">Click "Add Liability" to record loans or payables</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Liability Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Creditor</TableHead>
                      <TableHead className="text-right">Principal</TableHead>
                      <TableHead className="text-right">Outstanding</TableHead>
                      <TableHead className="text-right">Accrued Interest</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {liabilities.map((liability) => (
                      <TableRow key={liability.id}>
                        <TableCell className="font-mono text-sm">{liability.liability_code}</TableCell>
                        <TableCell className="font-medium">
                          {liability.liability_name}
                          {liability.interest_rate > 0 && (
                            <span className="text-xs text-muted-foreground block">
                              @ {liability.interest_rate}% p.a.
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {liability.liability_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{liability.creditor_name || '-'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(liability.principal_amount)}</TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          {formatCurrency(liability.outstanding_amount)}
                        </TableCell>
                        <TableCell className="text-right text-orange-600">
                          {formatCurrency(liability.accrued_interest)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            liability.status === 'active' ? 'bg-yellow-100 text-yellow-800' : 
                            liability.status === 'closed' ? 'bg-green-100 text-green-800' : 
                            'bg-red-100 text-red-800'
                          }>
                            {liability.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Depreciation Schedule Tab */}
            <TabsContent value="depreciation">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Depreciation Schedule</h3>
                <Button variant="outline" onClick={fetchDepreciationSchedule} size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>

              {depSchedule.assets?.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <TrendingDown className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No depreciable assets found</p>
                  <p className="text-sm">Add assets with depreciation rate to see schedule</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Asset Code</TableHead>
                        <TableHead>Asset Name</TableHead>
                        <TableHead className="text-right">Purchase Value</TableHead>
                        <TableHead className="text-right">Current Value</TableHead>
                        <TableHead className="text-right">Accumulated Dep.</TableHead>
                        <TableHead className="text-center">Rate (%)</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead className="text-right">Annual Dep.</TableHead>
                        <TableHead>Last Calculated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {depSchedule.assets?.map((asset) => (
                        <TableRow key={asset.asset_code}>
                          <TableCell className="font-mono text-sm">{asset.asset_code}</TableCell>
                          <TableCell className="font-medium">{asset.asset_name}</TableCell>
                          <TableCell className="text-right">{formatCurrency(asset.purchase_value)}</TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(asset.current_value)}</TableCell>
                          <TableCell className="text-right text-orange-600">
                            {formatCurrency(asset.accumulated_depreciation)}
                          </TableCell>
                          <TableCell className="text-center">{asset.depreciation_rate}%</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {asset.depreciation_method === 'straight_line' ? 'Straight Line' : 'Reducing Balance'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(asset.annual_depreciation)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {asset.last_depreciation_date || 'Never'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Summary */}
                  <div className="mt-6 p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-3">Summary</h4>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Purchase Value</p>
                        <p className="text-lg font-bold">{formatCurrency(depSchedule.summary?.total_purchase_value)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Current Value</p>
                        <p className="text-lg font-bold text-green-600">{formatCurrency(depSchedule.summary?.total_current_value)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Accumulated Depreciation</p>
                        <p className="text-lg font-bold text-orange-600">{formatCurrency(depSchedule.summary?.total_accumulated_depreciation)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Annual Depreciation</p>
                        <p className="text-lg font-bold text-red-600">{formatCurrency(depSchedule.summary?.total_annual_depreciation)}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add Asset Dialog */}
      <Dialog open={showAddAsset} onOpenChange={setShowAddAsset}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Asset</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Asset Name *</Label>
              <Input
                value={assetForm.asset_name}
                onChange={(e) => setAssetForm({ ...assetForm, asset_name: e.target.value })}
                placeholder="e.g., Dell Laptop, Office Chair"
                data-testid="asset-name-input"
              />
            </div>
            <div>
              <Label>Category *</Label>
              <Select
                value={assetForm.category}
                onValueChange={(v) => setAssetForm({ ...assetForm, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Asset Type</Label>
              <Select
                value={assetForm.asset_type}
                onValueChange={(v) => setAssetForm({ ...assetForm, asset_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed_asset">Fixed Asset</SelectItem>
                  <SelectItem value="current_asset">Current Asset</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Purchase Date *</Label>
              <Input
                type="date"
                value={assetForm.purchase_date}
                onChange={(e) => setAssetForm({ ...assetForm, purchase_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Purchase Value *</Label>
              <Input
                type="number"
                value={assetForm.purchase_value}
                onChange={(e) => setAssetForm({ ...assetForm, purchase_value: e.target.value })}
                placeholder="Amount in INR"
                data-testid="asset-value-input"
              />
            </div>
            <div>
              <Label>Depreciation Rate (% per year)</Label>
              <Input
                type="number"
                value={assetForm.depreciation_rate}
                onChange={(e) => setAssetForm({ ...assetForm, depreciation_rate: parseFloat(e.target.value) || 0 })}
                placeholder="e.g., 10, 15, 20"
              />
            </div>
            <div>
              <Label>Depreciation Method</Label>
              <Select
                value={assetForm.depreciation_method}
                onValueChange={(v) => setAssetForm({ ...assetForm, depreciation_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="straight_line">Straight Line Method</SelectItem>
                  <SelectItem value="reducing_balance">Reducing Balance Method</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={assetForm.location}
                onChange={(e) => setAssetForm({ ...assetForm, location: e.target.value })}
                placeholder="e.g., Admin Office, Lab 1"
              />
            </div>
            <div>
              <Label>Serial Number</Label>
              <Input
                value={assetForm.serial_number}
                onChange={(e) => setAssetForm({ ...assetForm, serial_number: e.target.value })}
                placeholder="Asset serial/tag number"
              />
            </div>
            <div>
              <Label>Vendor Name</Label>
              <Input
                value={assetForm.vendor_name}
                onChange={(e) => setAssetForm({ ...assetForm, vendor_name: e.target.value })}
                placeholder="Seller/Supplier name"
              />
            </div>
            <div>
              <Label>Warranty Expiry</Label>
              <Input
                type="date"
                value={assetForm.warranty_expiry}
                onChange={(e) => setAssetForm({ ...assetForm, warranty_expiry: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Remarks</Label>
              <Input
                value={assetForm.remarks}
                onChange={(e) => setAssetForm({ ...assetForm, remarks: e.target.value })}
                placeholder="Any additional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAsset(false)}>Cancel</Button>
            <Button onClick={handleAddAsset} data-testid="save-asset-btn">Add Asset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Liability Dialog */}
      <Dialog open={showAddLiability} onOpenChange={setShowAddLiability}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Liability</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Liability Name *</Label>
              <Input
                value={liabilityForm.liability_name}
                onChange={(e) => setLiabilityForm({ ...liabilityForm, liability_name: e.target.value })}
                placeholder="e.g., Building Loan, Equipment Advance"
                data-testid="liability-name-input"
              />
            </div>
            <div>
              <Label>Type *</Label>
              <Select
                value={liabilityForm.liability_type}
                onValueChange={(v) => setLiabilityForm({ ...liabilityForm, liability_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIABILITY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Creditor Name</Label>
              <Input
                value={liabilityForm.creditor_name}
                onChange={(e) => setLiabilityForm({ ...liabilityForm, creditor_name: e.target.value })}
                placeholder="Bank/Lender name"
              />
            </div>
            <div>
              <Label>Principal Amount *</Label>
              <Input
                type="number"
                value={liabilityForm.principal_amount}
                onChange={(e) => setLiabilityForm({ ...liabilityForm, principal_amount: e.target.value })}
                placeholder="Amount in INR"
                data-testid="liability-amount-input"
              />
            </div>
            <div>
              <Label>Interest Rate (% per year)</Label>
              <Input
                type="number"
                step="0.1"
                value={liabilityForm.interest_rate}
                onChange={(e) => setLiabilityForm({ ...liabilityForm, interest_rate: e.target.value })}
                placeholder="e.g., 10.5"
              />
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={liabilityForm.start_date}
                onChange={(e) => setLiabilityForm({ ...liabilityForm, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Maturity Date</Label>
              <Input
                type="date"
                value={liabilityForm.maturity_date}
                onChange={(e) => setLiabilityForm({ ...liabilityForm, maturity_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Payment Frequency</Label>
              <Select
                value={liabilityForm.payment_frequency}
                onValueChange={(v) => setLiabilityForm({ ...liabilityForm, payment_frequency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="one_time">One Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Remarks</Label>
              <Input
                value={liabilityForm.remarks}
                onChange={(e) => setLiabilityForm({ ...liabilityForm, remarks: e.target.value })}
                placeholder="Purpose of loan, terms, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLiability(false)}>Cancel</Button>
            <Button onClick={handleAddLiability} data-testid="save-liability-btn">Add Liability</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssetsLiabilitiesTab;
